import React, { useEffect, useState } from 'react';
import { Box, MenuItem, Select, Typography, FormControl, InputLabel, CircularProgress } from '@mui/material';
import ModalDialog from '../ModalDialog/ModalDialog';
import JlrButton from '../JlrButton/JlrButton';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { DARK_MODE } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMrDestinationStations } from '../../redux/actionCreators/majorRepair';

/**
 * MoveOutModal - Modal for selecting destination station when performing MOVE OUT from Major Repair
 * Props:
 *   open           - boolean, controls visibility
 *   onClose        - called when modal is closed/cancelled
 *   onConfirm      - called with selected station object { dtStationId, dtLineId, description }
 *   sequenceNumber - the ASN of the vehicle being moved out (used to fetch eligible stations via GET /mr/station/{sequenceNumber})
 */
const MoveOutModal = ({ open, onClose, onConfirm, sequenceNumber }) => {
	const { scaleValue } = useResolutionScale();
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const dispatch = useDispatch();

	const [stations, setStations] = useState([]);
	const [selectedStation, setSelectedStation] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (open && sequenceNumber) {
			setLoading(true);
			setError('');
			setSelectedStation('');
			dispatch(
				fetchMrDestinationStations(
					sequenceNumber,
					(data) => {
						setLoading(false);
						const list = Array.isArray(data) ? data : [];
						setStations(list);
						if (list.length > 0) {
							// Auto-select first eligible station
							setSelectedStation(list[0].dtStationId);
						}
					},
					(err) => {
						setLoading(false);
						setError(err?.response?.data?.message || 'Unable to load destination stations.');
					},
				),
			);
		}
	}, [open, sequenceNumber, dispatch]);

	const handleConfirm = () => {
		if (!selectedStation) return;
		// Pass the full station object so caller has dtStationId + dtLineId + description
		const stationObj = stations.find((st) => st.dtStationId === selectedStation) || { dtStationId: selectedStation };
		onConfirm(stationObj);
	};

	return (
		<ModalDialog
			open={open}
			title="Major Repair - Move Out"
			onClose={onClose}
			modalWidth={scaleValue(650)}
			modalHeight="auto"
		>
			<Box sx={{ p: 4, textAlign: 'center' }}>
				<Typography
					variant="h5"
					sx={{
						mb: 3,
						fontWeight: 600,
						color: themeMode === DARK_MODE ? '#fff' : '#222',
						fontSize: scaleValue(26),
					}}
				>
					Select Destination Station to Move Out
				</Typography>

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
						<CircularProgress size={36} />
					</Box>
				) : error ? (
					<Typography color="error" sx={{ mb: 3 }}>
						{error}
					</Typography>
				) : (
					<FormControl fullWidth sx={{ my: 3, textAlign: 'left' }}>
						<InputLabel id="destination-station-label">Destination Station</InputLabel>
						<Select
							labelId="destination-station-label"
							value={selectedStation}
							label="Destination Station"
							onChange={(e) => setSelectedStation(e.target.value)}
							sx={{
								color: themeMode === DARK_MODE ? '#fff' : '#000',
							}}
						>
							{stations.length === 0 ? (
								<MenuItem value="" disabled>
									No eligible stations available
								</MenuItem>
							) : (
								stations.map((st, idx) => (
									<MenuItem key={idx} value={st.dtStationId}>
										{st.description || `Station ${st.dtStationId}`}
									</MenuItem>
								))
							)}
						</Select>
					</FormControl>
				)}

				<Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
					<JlrButton
						variant="outlined"
						size="medium"
						onClick={onClose}
						sx={{ minWidth: scaleValue(140), width: 'auto' }}
					>
						Cancel
					</JlrButton>
					<JlrButton
						variant="primary"
						size="medium"
						onClick={handleConfirm}
						disabled={loading || !selectedStation}
						sx={{ minWidth: scaleValue(140), width: 'auto' }}
					>
						Confirm Move Out
					</JlrButton>
				</Box>
			</Box>
		</ModalDialog>
	);
};

export default MoveOutModal;
